import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, BadgeCheck, Hash } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { operatorLoginSchema, type OperatorLoginValues } from '../auth.schemas';
import { resolveAuthErrorMessage } from '../auth.messages';
import { useLogin } from '../hooks/use-login';

interface OperatorLoginFormProps {
  onAuthenticated: () => void;
}

/**
 * Acceso de operadores (griferos, despachadores).
 *
 * El operador se identifica únicamente con su código asignado, tal como funciona
 * el panel actual: no requiere correo ni contraseña.
 */
export function OperatorLoginForm({ onAuthenticated }: OperatorLoginFormProps) {
  const login = useLogin();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OperatorLoginValues>({
    resolver: zodResolver(operatorLoginSchema),
    defaultValues: { identity: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);

    try {
      await login.mutateAsync({ identity: values.identity, type: 'operator' });
      onAuthenticated();
    } catch (error) {
      setServerError(resolveAuthErrorMessage(error));
    }
  });

  const isBusy = login.isPending;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      {serverError ? (
        <div className="animate-shake">
          <Alert variant="error">{serverError}</Alert>
        </div>
      ) : null}

      <Input
        label="Código de acceso"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="Ej. 108857574383"
        leftIcon={<Hash className="size-4.5" />}
        hint="Es el código asignado a tu usuario en EDSuite."
        error={errors.identity?.message}
        disabled={isBusy}
        autoFocus
        {...register('identity')}
      />

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={isBusy}
        className="mt-1"
        rightIcon={<ArrowRight className="size-4" />}
      >
        Ingresar al turno
      </Button>

      <p className="flex items-start gap-1.5 text-xs text-ink-400">
        <BadgeCheck className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        Si eres administrador, usa la pestaña <strong>Administrador</strong>.
      </p>
    </form>
  );
}
